import { TextInfoGroupAlt } from "@/components/shared";
import { CircleLoader } from "@/components/shared/loaders";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardTitle, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Billing, GenerateCustomerPortalLinkQuery, GenerateCustomerPortalLinkQueryVariables, GenerateCustomerPortalLinkDocument } from "@/graphql/__generated__/graphql";
import { toNormalCase } from "@/utils/helpers";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLazyQuery } from "@apollo/client/react";

interface ContractDetailsProps {
  billing: Partial<Billing> | undefined | null;
  billingLoading: boolean;
}

function ContractDetails({ billing, billingLoading }: ContractDetailsProps) {
  const { t } = useTranslation();
  const [shouldOpenPortal, setShouldOpenPortal] = useState(false);
  const [generateCustomerPortalLink, { data: customerPortalLinkData, loading: customerPortalLinkLoading }] = useLazyQuery<GenerateCustomerPortalLinkQuery, GenerateCustomerPortalLinkQueryVariables>(GenerateCustomerPortalLinkDocument);

  const handleGenerateClick = async () => {
    setShouldOpenPortal(true);
    generateCustomerPortalLink();
  };

  useEffect(() => {
    if (customerPortalLinkData && customerPortalLinkData.generateCustomerPortalLink && shouldOpenPortal) {
      window.open(customerPortalLinkData.generateCustomerPortalLink, "_blank", "noopener,noreferrer");
      setShouldOpenPortal(false);
    }
  }, [customerPortalLinkData, shouldOpenPortal]);

  if (billingLoading) {
    return <Skeleton className="h-[100px] w-full" />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold">{t("settings.billing.contractdetails.title")}</CardTitle>
        <CardDescription>{t("settings.billing.contractdetails.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        {
          !billing ? (
            <div>
              <p>{t("settings.billing.contractdetails.nobilling")}</p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex flex-row gap-4">
                <TextInfoGroupAlt
                  label={t("settings.billing.contractdetails.subscriptionstatus")}
                  content={toNormalCase(billing.subscriptionStatus || "")}
                />
                <TextInfoGroupAlt
                  label={t("settings.billing.contractdetails.currentseatcount")}
                  content={billing.currentSeatCount}
                />
              </div>

              {
                <div>
                  <p className="text-sm mb-2 text-muted-foreground">{t("settings.billing.contractdetails.portaldescription")}</p>
                  <Button
                    variant="outline"
                    onClick={handleGenerateClick}
                    disabled={customerPortalLinkLoading}
                  >
                    {customerPortalLinkLoading ? (
                      <div className="flex items-center gap-2">
                        <CircleLoader
                          visible={true}
                          height={20}
                          width={20}
                          strokeWidth={4}
                          ariaLabel="circle-loader"
                        />
                        {t("settings.billing.contractdetails.pleasewait")}
                      </div>
                    ) : (
                      t("settings.billing.contractdetails.opencustomerportal")
                    )}
                  </Button>
                </div>
              }
            </div>
          )
        }

      </CardContent>
    </Card>
  );
}

export default ContractDetails;
